/**
 * The storefront's built-in copy in languages other than English.
 *
 * English stays where it always was - the `value` of every field in the
 * manifests, gathered into `CONTENT_DEFAULTS`. Each other language is one file,
 * `locales/<code>.ts`, carrying the same full keys (`shop.cart.title`,
 * `hyper.footer.column.help`) and nothing else, lazily imported so a shopper
 * reading English never downloads Tamil.
 *
 * Resolution (docs/i18n.md §4, implemented in `useContent`): the merchant's
 * override for the language → this dictionary → the English default → the key
 * humanised. A locale file that lags behind a new manifest field is therefore
 * safe: that one string renders in English until it is translated, and
 * `tests/storefrontContentLocales.test.ts` fails until it is.
 */

type LocaleModule = { default: Readonly<Record<string, string>> };

const modules = import.meta.glob<LocaleModule>('./locales/*.ts');

function codeOf(path: string): string {
  // './locales/hi.ts' -> 'hi'
  return path.slice(path.lastIndexOf('/') + 1).replace(/\.ts$/, '');
}

const loaders = new Map(Object.entries(modules).map(([path, load]) => [codeOf(path), load]));

const cache = new Map<string, Readonly<Record<string, string>>>();
const pending = new Map<string, Promise<void>>();

/** Language codes with a built-in storefront dictionary (English excluded - it is the manifests). */
export const STOREFRONT_LOCALE_CODES: readonly string[] = [...loaders.keys()].sort();

/** True for English, a language already loaded, or one with no dictionary to load. */
export function isStorefrontLocaleLoaded(code: string): boolean {
  return !loaders.has(code) || cache.has(code);
}

/** The loaded dictionary for a language, or undefined (English, unknown, or not loaded yet). */
export function storefrontLocaleDictionary(code: string): Readonly<Record<string, string>> | undefined {
  return cache.get(code);
}

/**
 * Load a language's storefront copy once. Never rejects: a chunk that fails
 * to download leaves English in place and can be retried by calling again.
 */
export function loadStorefrontLocale(code: string): Promise<void> {
  const load = loaders.get(code);
  if (!load || cache.has(code)) return Promise.resolve();
  const inFlight = pending.get(code);
  if (inFlight) return inFlight;

  const started = load()
    .then((module) => {
      cache.set(code, module.default);
    })
    .catch((error: unknown) => {
      if (import.meta.env.DEV) console.warn(`[i18n] Could not load storefront copy for "${code}"`, error);
    })
    .finally(() => {
      pending.delete(code);
    });
  pending.set(code, started);
  return started;
}
